import { useCallback, useEffect, useMemo, useRef } from "react";
import { buildHeadsUpBaseContextPayload } from "@/components/heads-up/analytics";
import {
  trackEvent,
  TrackedEvent,
  type HeadsUpAnalyticsContextEventParams,
  type HeadsUpLiveSessionEndReason,
} from "@/domains/metrics";
import type { AppStore } from "@/store/AppStore";
import { isAbortError } from "@/modules/api/errorHandling";
import { logger } from "@/modules/logger";
import { buildTranscriptPayload } from "@/modules/audio-recording/transcripts";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";
import type { HeadsUpContextInfo } from "./use-context-id";

const MIN_TRANSCRIPT_LENGTH = 250;
const MIN_NEW_CONTENT_LENGTH = 250;
const POLL_INTERVAL_MS = 5_000;
const MIN_SEND_INTERVAL_MS = 15_000;
const FAILURE_BACKOFF_MAX_MS = 120_000;

interface UseLiveCardsArgs {
  store: AppStore;
  contextInfo: HeadsUpContextInfo;
  activeNoteTitle: string | null;
  smartViewId: string | null;
}

interface UseLiveCardsResult {
  isLiveActive: boolean;
}

interface LiveSessionState {
  startedAt: number;
  contextId: string;
  activeNoteId: string;
  recordingId: string;
  basePayload: HeadsUpAnalyticsContextEventParams;
}

function getLiveSessionEndReason({
  hasActiveRecording,
  activeRecordingId,
  activeRecordingNoteId,
  activeNoteId,
  isEnabled,
  isDatabaseReady,
  spaceAccountId,
  contextId,
}: {
  hasActiveRecording: boolean;
  activeRecordingId: string | null;
  activeRecordingNoteId: string | null;
  activeNoteId: string | null;
  isEnabled: boolean;
  isDatabaseReady: boolean;
  spaceAccountId: string | null;
  contextId: string | null;
}): HeadsUpLiveSessionEndReason {
  if (!hasActiveRecording || !activeRecordingId) {
    return "recording_stopped";
  }

  if (activeRecordingNoteId !== activeNoteId) {
    return "note_changed";
  }

  if (!isEnabled || !isDatabaseReady || !spaceAccountId) {
    return "live_unavailable";
  }

  if (!contextId) {
    return "context_changed";
  }

  return "unmount";
}

export function useLiveCards({
  store,
  contextInfo,
  activeNoteTitle,
  smartViewId,
}: UseLiveCardsArgs): UseLiveCardsResult {
  const activeRequestAbortControllerRef = useRef<AbortController | null>(null);
  const lastTranscriptLenRef = useRef(0);
  const lastSentAtRef = useRef(0);
  const isCallingRef = useRef(false);
  const scheduledSendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestTranscriptTextRef = useRef<string | null>(null);
  const latestTranscriptArrayRef = useRef<TranscriptArrayItem[]>([]);
  const latestTranscriptLenRef = useRef(0);
  const consecutiveFailuresRef = useRef(0);
  const nextAllowedSendAtRef = useRef(0);
  const liveSessionRef = useRef<LiveSessionState | null>(null);

  // Interval/timeout callbacks run after render; keep latest inputs in refs to avoid stale closures.
  const contextInfoRef = useRef(contextInfo);
  const activeRecordingIdRef = useRef(store.audio.activeRecordingId);
  contextInfoRef.current = contextInfo;
  activeRecordingIdRef.current = store.audio.activeRecordingId;

  useEffect(() => {
    lastTranscriptLenRef.current = 0;
    lastSentAtRef.current = 0;
    latestTranscriptTextRef.current = null;
    latestTranscriptArrayRef.current = [];
    latestTranscriptLenRef.current = 0;
    isCallingRef.current = false;
    consecutiveFailuresRef.current = 0;
    nextAllowedSendAtRef.current = 0;
    if (scheduledSendTimerRef.current) {
      clearTimeout(scheduledSendTimerRef.current);
      scheduledSendTimerRef.current = null;
    }
    if (activeRequestAbortControllerRef.current) {
      activeRequestAbortControllerRef.current.abort();
      activeRequestAbortControllerRef.current = null;
    }
  }, [contextInfo.contextId, store.audio.activeRecordingId]);

  const isEnabled = store.spaceAccountFeatureFlags.getFlagStatus("heads-up-live") ?? false;
  const hasActiveRecording = store.audio.hasActiveRecording;
  const activeRecordingNoteId = store.audio.activeRecordingNoteId;
  const activeRecordingId = store.audio.activeRecordingId;
  const isRecordingOnActiveNote =
    hasActiveRecording && activeRecordingNoteId === contextInfo.activeNoteId;

  const shouldBeActive =
    isEnabled &&
    contextInfo.isDatabaseReady &&
    isRecordingOnActiveNote &&
    !!contextInfo.contextId &&
    !!activeRecordingId &&
    !!contextInfo.spaceAccountId;

  const isLiveActive = shouldBeActive;

  const currentBasePayload = useMemo(
    () =>
      buildHeadsUpBaseContextPayload({
        contextId: contextInfo.contextId,
        contextNoteId: contextInfo.activeNoteId,
        contextNoteTitle: activeNoteTitle,
        smartViewId,
        activeSpaceAccountId: contextInfo.spaceAccountId,
      }),
    [
      contextInfo.contextId,
      contextInfo.activeNoteId,
      activeNoteTitle,
      smartViewId,
      contextInfo.spaceAccountId,
    ]
  );

  const endLiveSession = useCallback((endReason: HeadsUpLiveSessionEndReason) => {
    const currentSession = liveSessionRef.current;
    if (!currentSession) return;

    trackEvent(TrackedEvent.HeadsUpLiveSessionEnd, {
      ...currentSession.basePayload,
      recording_id: currentSession.recordingId,
      duration_ms: Math.max(0, Date.now() - currentSession.startedAt),
      end_reason: endReason,
    });
    liveSessionRef.current = null;
  }, []);

  const startLiveSession = useCallback(() => {
    if (!activeRecordingId || !contextInfo.contextId || !contextInfo.activeNoteId) return;

    const nextSession: LiveSessionState = {
      startedAt: Date.now(),
      contextId: contextInfo.contextId,
      activeNoteId: contextInfo.activeNoteId,
      recordingId: activeRecordingId,
      basePayload: currentBasePayload,
    };

    liveSessionRef.current = nextSession;
    trackEvent(TrackedEvent.HeadsUpLiveSessionStart, {
      ...nextSession.basePayload,
      recording_id: nextSession.recordingId,
    });
  }, [activeRecordingId, contextInfo.activeNoteId, contextInfo.contextId, currentBasePayload]);

  useEffect(() => {
    const activeSession = liveSessionRef.current;

    if (!shouldBeActive) {
      if (activeSession) {
        endLiveSession(
          getLiveSessionEndReason({
            hasActiveRecording,
            activeRecordingId,
            activeRecordingNoteId,
            activeNoteId: contextInfo.activeNoteId,
            isEnabled,
            isDatabaseReady: contextInfo.isDatabaseReady,
            spaceAccountId: contextInfo.spaceAccountId,
            contextId: contextInfo.contextId,
          })
        );
      }
      return;
    }

    if (!activeSession) {
      startLiveSession();
      return;
    }

    if (activeSession.recordingId !== activeRecordingId) {
      endLiveSession("recording_stopped");
      startLiveSession();
      return;
    }

    if (activeSession.activeNoteId !== contextInfo.activeNoteId) {
      endLiveSession("note_changed");
      startLiveSession();
      return;
    }

    if (activeSession.contextId !== contextInfo.contextId) {
      endLiveSession("context_changed");
      startLiveSession();
    }
  }, [
    shouldBeActive,
    hasActiveRecording,
    activeRecordingId,
    activeRecordingNoteId,
    contextInfo.activeNoteId,
    contextInfo.contextId,
    contextInfo.isDatabaseReady,
    contextInfo.spaceAccountId,
    isEnabled,
    endLiveSession,
    startLiveSession,
  ]);

  useEffect(() => {
    return () => {
      if (!liveSessionRef.current) return;
      endLiveSession("unmount");
    };
  }, [endLiveSession]);

  useEffect(() => {
    if (!shouldBeActive) return;

    logger.info({
      message: "[useLiveCards] Polling started",
      info: { contextId: contextInfo.contextId, activeRecordingId },
    });

    const interval = setInterval(async () => {
      if (isCallingRef.current) {
        return;
      }

      const contextId = contextInfoRef.current.contextId;
      const currentRecordingId = activeRecordingIdRef.current;
      if (!contextId || !currentRecordingId) {
        return;
      }

      try {
        const turns = await store.dexie.repositories.localAudioRecordingTranscriptTurns.table
          .where({ associatedAudioRecordingId: currentRecordingId })
          .sortBy("transcriptTurnIndex");

        const { transcriptArray, transcriptText } = buildTranscriptPayload(turns);

        if (transcriptText.length < MIN_TRANSCRIPT_LENGTH) {
          logger.debug({
            message: "[useLiveCards] Transcript below minimum length",
            info: { transcriptLength: transcriptText.length, minRequired: MIN_TRANSCRIPT_LENGTH },
          });
          return;
        }

        const newContentLen = transcriptText.length - lastTranscriptLenRef.current;
        if (newContentLen < MIN_NEW_CONTENT_LENGTH) {
          logger.debug({
            message: "[useLiveCards] Insufficient new content",
            info: { newContentLen, minRequired: MIN_NEW_CONTENT_LENGTH },
          });
          return;
        }

        latestTranscriptTextRef.current = transcriptText;
        latestTranscriptArrayRef.current = transcriptArray;
        latestTranscriptLenRef.current = transcriptText.length;

        if (scheduledSendTimerRef.current) {
          return;
        }

        const now = Date.now();
        const elapsedSinceLastSend = now - lastSentAtRef.current;
        const sendThrottleDelayMs = Math.max(0, MIN_SEND_INTERVAL_MS - elapsedSinceLastSend);
        const failureBackoffDelayMs = Math.max(0, nextAllowedSendAtRef.current - now);
        const delayMs = Math.max(sendThrottleDelayMs, failureBackoffDelayMs);
        logger.debug({
          message: "[useLiveCards] Scheduling live request",
          info: {
            contextId,
            delayMs,
            sendThrottleDelayMs,
            failureBackoffDelayMs,
            consecutiveFailures: consecutiveFailuresRef.current,
            transcriptLength: latestTranscriptLenRef.current,
          },
        });

        scheduledSendTimerRef.current = setTimeout(async () => {
          scheduledSendTimerRef.current = null;

          if (isCallingRef.current) {
            return;
          }

          const currentContextId = contextInfoRef.current.contextId;
          const currentTranscriptText = latestTranscriptTextRef.current;
          const currentTranscriptArray = latestTranscriptArrayRef.current;
          const currentTranscriptLen = latestTranscriptLenRef.current;
          const currentActiveNoteId = contextInfoRef.current.activeNoteId;
          const currentSpaceAccountId = contextInfoRef.current.spaceAccountId;
          const currentRecordingId = activeRecordingIdRef.current;
          const activeRecording = store.audio.activeRecording;
          const currentRecording =
            activeRecording?.id === currentRecordingId ? activeRecording : null;
          const recordingStartedAt = currentRecording?.locallyCreatedAt ?? null;
          const isDatabaseReady = contextInfoRef.current.isDatabaseReady;
          if (!currentContextId || !currentTranscriptText || !currentSpaceAccountId) {
            logger.debug({
              message: "[useLiveCards] Missing context values for live request; skipping",
              info: {
                contextId: currentContextId,
                hasSpaceAccountId: Boolean(currentSpaceAccountId),
              },
            });
            return;
          }
          if (!isDatabaseReady) {
            logger.debug({
              message: "[useLiveCards] Database not ready for live request; skipping",
              info: { contextId: currentContextId },
            });
            return;
          }

          isCallingRef.current = true;
          lastSentAtRef.current = Date.now();

          try {
            const abortController = new AbortController();
            activeRequestAbortControllerRef.current = abortController;

            logger.info({
              message: "[useLiveCards] Sending live request",
              info: { contextId: currentContextId, transcriptLength: currentTranscriptLen },
            });

            const response = await store.headsUp.requestLiveCardsForContext({
              contextId: currentContextId,
              transcriptText: currentTranscriptText,
              transcriptArray: currentTranscriptArray,
              spaceAccountId: currentSpaceAccountId,
              activeNoteId: currentActiveNoteId,
              recordingStartedAt,
              signal: abortController.signal,
            });

            if (contextInfoRef.current.contextId !== currentContextId) {
              logger.debug({
                message: "[useLiveCards] Context changed during request, discarding",
                info: {
                  requestContextId: currentContextId,
                  currentContextId: contextInfoRef.current.contextId,
                },
              });
              return;
            }

            consecutiveFailuresRef.current = 0;
            nextAllowedSendAtRef.current = 0;
            lastTranscriptLenRef.current = currentTranscriptLen;

            if (response) {
              logger.info({
                message: "[useLiveCards] Live cards persisted",
                info: {
                  contextId: currentContextId,
                  cardCount: response.smart_cards.length,
                },
              });
            } else {
              logger.debug({
                message: "[useLiveCards] No cards in response",
                info: { contextId: currentContextId },
              });
            }
          } catch (error) {
            if (isAbortError(error)) {
              logger.debug({
                message: "[useLiveCards] Live request aborted",
                info: { contextId: currentContextId },
              });
            } else {
              const consecutiveFailures = consecutiveFailuresRef.current + 1;
              consecutiveFailuresRef.current = consecutiveFailures;
              const backoffMs = Math.min(
                FAILURE_BACKOFF_MAX_MS,
                MIN_SEND_INTERVAL_MS * 2 ** (consecutiveFailures - 1)
              );
              nextAllowedSendAtRef.current = Date.now() + backoffMs;
              logger.warn({
                message: "[useLiveCards] Live request failed",
                info: {
                  contextId: currentContextId,
                  error: String(error),
                  consecutiveFailures,
                  backoffMs,
                },
              });
            }
          } finally {
            isCallingRef.current = false;
            activeRequestAbortControllerRef.current = null;
          }
        }, delayMs);
      } catch (error) {
        logger.warn({
          message: "[useLiveCards] Poll tick failed",
          info: { contextId, recordingId: currentRecordingId, error: String(error) },
        });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      logger.info({ message: "[useLiveCards] Polling stopped" });
      clearInterval(interval);
      if (scheduledSendTimerRef.current) {
        clearTimeout(scheduledSendTimerRef.current);
        scheduledSendTimerRef.current = null;
      }
      if (activeRequestAbortControllerRef.current) {
        activeRequestAbortControllerRef.current.abort();
        activeRequestAbortControllerRef.current = null;
      }
    };
  }, [shouldBeActive, activeRecordingId, contextInfo.contextId, store]);

  return { isLiveActive };
}
